import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  UserPlus,
  X,
} from 'lucide-react';
import { useCreateTeamMember, usePeople } from '../../hooks/usePeople';
import { useDepartments } from '../../hooks/useDepartments';
import { useRoleTemplates } from '../../hooks/useRoles';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import type { CreateTeamMemberResult } from '../../lib/peopleApi';
import type { RoleTemplate, UserPermissionMatrix } from '../../lib/permissionsApi';
import { emptyMatrix } from '../../lib/permissionsApi';
import { generatePassword, isStrongPassword } from '../../lib/passwordGenerator';
import { RbacMatrixEditor } from '../rbac/RbacMatrixEditor';

export interface CreateTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (result: CreateTeamMemberResult) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Create Team Member modal (CLE-216, parent Part 1 + 3.5). 2-column ~640px modal
 * matching the Create Lead modal style. Collects Personal Info, Work Info and
 * Account & Access (with an inline RBAC matrix). Async email-uniqueness check,
 * auto-generated password with show/copy, searchable Reports To, avatar URL,
 * Send welcome email. On success it surfaces the generated password in a
 * confirmation dialog when SMTP is not configured (the backend signals this by
 * returning a non-null `generatedPassword`).
 */
export function CreateTeamMemberModal({ open, onClose, onCreated }: CreateTeamMemberModalProps) {
  const { data: people } = usePeople({ active: 'true' });
  const { data: templates } = useRoleTemplates();
  const { data: departments } = useDepartments();
  const create = useCreateTeamMember();

  const nameRef = useRef<HTMLInputElement>(null);

  // --- Personal Info ---
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // --- Work Info ---
  const [title, setTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState('40');
  const [hourlyRate, setHourlyRate] = useState('');
  const [reportsToId, setReportsToId] = useState('');
  const [reportsToQuery, setReportsToQuery] = useState('');
  const [reportsToOpen, setReportsToOpen] = useState(false);
  const [startDate, setStartDate] = useState('');

  // --- Account & Access ---
  const [createAccount, setCreateAccount] = useState(false);
  // Invite (email link, user sets own password) vs manual password (SaaS hardening).
  const [accountMode, setAccountMode] = useState<'invite' | 'password'>('invite');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [rbacOpen, setRbacOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<UserPermissionMatrix>(() => emptyMatrix());

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Async email-uniqueness state.
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'taken' | 'ok'>('idle');
  // Post-success password dialog (SMTP unconfigured / opted out).
  const [passwordDialog, setPasswordDialog] = useState<{ email: string; password: string } | null>(
    null,
  );
  // Post-success invite confirmation ("Invitație trimisă la …").
  const [inviteDialog, setInviteDialog] = useState<{ email: string } | null>(null);

  const peopleList = useMemo(() => people ?? [], [people]);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setFullName('');
    setEmail('');
    setPhone('');
    setAvatarUrl('');
    setTitle('');
    setDepartmentId('');
    setLocation('');
    setCapacity('40');
    setHourlyRate('');
    setReportsToId('');
    setReportsToQuery('');
    setReportsToOpen(false);
    setStartDate('');
    setCreateAccount(false);
    setAccountMode('invite');
    setPassword('');
    setShowPassword(false);
    setSendWelcomeEmail(true);
    setRbacOpen(false);
    setTemplateId(null);
    setMatrix(emptyMatrix());
    setErrors({});
    setSubmitError(null);
    setEmailStatus('idle');
    setPasswordDialog(null);
    setInviteDialog(null);
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => nameRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (passwordDialog) setPasswordDialog(null);
        else if (inviteDialog) {
          setInviteDialog(null);
          onClose();
        } else onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, passwordDialog, inviteDialog]);

  // Async email-uniqueness check (debounced, client-side against the directory).
  // The backend is the authority (409 on submit), but this gives instant feedback.
  useEffect(() => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setEmailStatus('idle');
      return;
    }
    setEmailStatus('checking');
    const t = setTimeout(() => {
      const taken = peopleList.some((p) => p.email.toLowerCase() === trimmed);
      setEmailStatus(taken ? 'taken' : 'ok');
    }, 350);
    return () => clearTimeout(t);
  }, [email, peopleList]);

  // When the manual-password mode is active with no password yet, auto-generate one.
  useEffect(() => {
    if (createAccount && accountMode === 'password' && !password) {
      setPassword(generatePassword());
    }
  }, [createAccount, accountMode, password]);

  if (!open) return null;

  const reportsToMatches = peopleList
    .filter((p) => p.fullName.toLowerCase().includes(reportsToQuery.trim().toLowerCase()))
    .slice(0, 8);
  const reportsToSelected = peopleList.find((p) => p.id === reportsToId);

  function regeneratePassword() {
    setPassword(generatePassword());
    setCopied(false);
  }

  async function copyPassword(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }

  function handleSelectTemplate(id: string | null, tmpl: RoleTemplate | null) {
    setTemplateId(id);
    void tmpl; // matrix pre-fill is handled inside RbacMatrixEditor
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!fullName.trim()) next.fullName = 'Numele complet este obligatoriu';
    const trimmedEmail = email.trim();
    if (!trimmedEmail) next.email = 'Email-ul este obligatoriu';
    else if (!EMAIL_RE.test(trimmedEmail)) next.email = 'Introdu o adresă de email validă';
    else if (emailStatus === 'taken')
      next.email = 'Există deja un membru al echipei cu acest email';
    if (capacity.trim()) {
      const n = Number(capacity);
      if (!Number.isFinite(n) || n < 0)
        next.capacity = 'Capacitatea trebuie să fie un număr nenegativ';
    }
    if (hourlyRate.trim()) {
      const n = Number(hourlyRate);
      if (!Number.isFinite(n) || n < 0)
        next.hourlyRate = 'Tariful trebuie să fie un număr nenegativ';
    }
    if (createAccount && accountMode === 'password' && password && !isStrongPassword(password)) {
      next.password = 'Minim 8 caractere, cu 1 literă mare și 1 cifră';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const isValid =
    fullName.trim().length > 0 &&
    EMAIL_RE.test(email.trim()) &&
    emailStatus !== 'taken' &&
    (!createAccount || accountMode === 'invite' || !password || isStrongPassword(password));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (create.isPending) return;
    if (!validate()) return;

    create.mutate(
      {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        title: title.trim() || null,
        departmentId: departmentId || null,
        location: location.trim() || null,
        capacity: capacity.trim() ? Number(capacity) : undefined,
        hourlyRate: hourlyRate.trim() ? Number(hourlyRate) : null,
        reportsToId: reportsToId || null,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        avatarUrl: avatarUrl.trim() || null,
        createAccount,
        accountMode: createAccount ? accountMode : undefined,
        password: createAccount && accountMode === 'password' && password ? password : undefined,
        sendWelcomeEmail: createAccount && accountMode === 'password' ? sendWelcomeEmail : false,
        roleTemplateId: createAccount ? templateId : null,
        customPermissions: createAccount ? matrix : null,
      },
      {
        onSuccess: (result) => {
          onCreated?.(result);
          if (result.invited) {
            // Invite flow: the user sets their own password via the emailed link.
            setInviteDialog({ email: email.trim() });
          } else if (result.generatedPassword) {
            // If the backend returned the password (SMTP unconfigured / opted out,
            // or the invite fell back to password mode), show it in a dialog.
            setPasswordDialog({ email: email.trim(), password: result.generatedPassword });
          } else {
            onClose();
          }
        },
        onError: (err) => {
          setSubmitError(err instanceof Error ? err.message : 'Crearea membrului echipei a eșuat');
        },
      },
    );
  }

  const errText = (key: string) =>
    errors[key] ? <p className="mt-1 text-sm text-red-600">{errors[key]}</p> : null;

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-member-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !passwordDialog && !inviteDialog) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[640px] max-h-[90vh] p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2
            id="create-member-title"
            className="flex items-center gap-2 text-lg font-semibold text-gray-900"
          >
            <UserPlus size={18} className="text-brand-600" />
            Adaugă membru
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[calc(90vh-9rem)] overflow-y-auto px-5 py-4">
            {/* Two columns: Personal Info / Work Info */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {/* ---- Left: Personal Info ---- */}
              <div className="flex flex-col gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Informații personale
                </h3>

                <label className="block text-sm font-medium text-gray-700">
                  Nume complet <span className="text-red-500">*</span>
                  <input
                    ref={nameRef}
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="ex., Ana Popescu"
                    className="ui-input mt-1"
                    aria-invalid={Boolean(errors.fullName)}
                  />
                </label>
                {errText('fullName')}

                <label className="block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                  <div className="relative mt-1">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ana@company.com"
                      className="ui-input pr-9"
                      aria-invalid={Boolean(errors.email)}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      {emailStatus === 'checking' && (
                        <RefreshCw size={14} className="animate-spin text-gray-400" />
                      )}
                      {emailStatus === 'ok' && <Check size={15} className="text-emerald-500" />}
                      {emailStatus === 'taken' && <X size={15} className="text-red-500" />}
                    </span>
                  </div>
                </label>
                {emailStatus === 'taken' && !errors.email && (
                  <p className="-mt-2 text-sm text-red-600">
                    Există deja un membru al echipei cu acest email
                  </p>
                )}
                {errText('email')}

                <label className="block text-sm font-medium text-gray-700">
                  Telefon
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+40 7XX XXX XXX"
                    className="ui-input mt-1"
                  />
                </label>

                <label className="block text-sm font-medium text-gray-700">
                  URL avatar
                  <input
                    type="url"
                    value={avatarUrl}
                    onChange={(e) => setAvatarUrl(e.target.value)}
                    placeholder="https://…/avatar.png"
                    className="ui-input mt-1"
                  />
                  {avatarUrl.trim() && (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="mt-2 h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </label>
              </div>

              {/* ---- Right: Work Info ---- */}
              <div className="flex flex-col gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Informații profesionale
                </h3>

                <label className="block text-sm font-medium text-gray-700">
                  Funcție
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="ex., Senior Developer"
                    className="ui-input mt-1"
                  />
                </label>

                <Select
                  label="Departament"
                  clearable
                  value={departmentId}
                  onChange={setDepartmentId}
                  placeholder="Fără departament"
                  options={(departments ?? []).map((d) => ({
                    value: d.id,
                    label: d.name,
                    color: d.color,
                  }))}
                />

                <label className="block text-sm font-medium text-gray-700">
                  Locație
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="ex., București"
                    className="ui-input mt-1"
                  />
                </label>

                <div className="flex gap-2">
                  <label className="flex-1 text-sm font-medium text-gray-700">
                    Capacitate (h/săpt)
                    <input
                      type="number"
                      min={0}
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      className="ui-input mt-1"
                      aria-invalid={Boolean(errors.capacity)}
                    />
                  </label>
                  <label className="flex-1 text-sm font-medium text-gray-700">
                    Tarif orar
                    <input
                      type="number"
                      min={0}
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      placeholder="0"
                      className="ui-input mt-1"
                      aria-invalid={Boolean(errors.hourlyRate)}
                    />
                  </label>
                </div>
                {errText('capacity')}
                {errText('hourlyRate')}

                {/* Reports To — searchable dropdown */}
                <div className="block text-sm font-medium text-gray-700">
                  Raportează către
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={reportsToSelected ? reportsToSelected.fullName : reportsToQuery}
                      onChange={(e) => {
                        setReportsToQuery(e.target.value);
                        setReportsToId('');
                        setReportsToOpen(true);
                      }}
                      onFocus={() => setReportsToOpen(true)}
                      onBlur={() => setTimeout(() => setReportsToOpen(false), 150)}
                      placeholder="Caută un manager…"
                      className="ui-input"
                    />
                    {reportsToSelected && (
                      <button
                        type="button"
                        onClick={() => {
                          setReportsToId('');
                          setReportsToQuery('');
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600"
                        aria-label="Șterge"
                      >
                        <X size={14} />
                      </button>
                    )}
                    {reportsToOpen && reportsToMatches.length > 0 && (
                      <ul className="absolute z-20 mt-1 max-h-44 w-full overflow-y-auto rounded-md border border-border bg-card py-1 shadow-lg">
                        {reportsToMatches.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setReportsToId(p.id);
                                setReportsToQuery('');
                                setReportsToOpen(false);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                            >
                              <span className="font-medium text-gray-900">{p.fullName}</span>
                              {p.title && (
                                <span className="text-xs text-gray-400">· {p.title}</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <label className="block text-sm font-medium text-gray-700">
                  Data începerii
                  <DateField value={startDate} onChange={setStartDate} className="mt-1 w-full" />
                </label>
              </div>
            </div>

            {/* ---- Account & Access ---- */}
            <div className="mt-6 rounded-lg border border-border p-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={createAccount}
                  onChange={(e) => setCreateAccount(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                Creează cont de autentificare
                <span className="font-normal text-gray-400">
                  — generează un utilizator, parolă &amp; permisiuni
                </span>
              </label>

              {createAccount && (
                <div className="mt-4 flex flex-col gap-4">
                  {/* Account mode: invite email vs manual password */}
                  <div className="flex flex-col gap-2 text-sm text-gray-700">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="account-mode"
                        checked={accountMode === 'invite'}
                        onChange={() => setAccountMode('invite')}
                        className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      Trimite invitație pe email
                      <span className="font-normal text-gray-400">
                        — persoana își setează singură parola
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="account-mode"
                        checked={accountMode === 'password'}
                        onChange={() => setAccountMode('password')}
                        className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      Setează parola manual
                    </label>
                  </div>

                  {accountMode === 'invite' && (
                    <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-500">
                      Persoana va primi un email cu un link (valabil 7 zile) pentru a-și activa
                      contul și a-și seta parola. Dacă SMTP nu este configurat, se revine automat la
                      modul cu parolă.
                    </p>
                  )}

                  {/* Password (manual mode only) */}
                  {accountMode === 'password' && (
                    <div className="block text-sm font-medium text-gray-700">
                      Parolă
                      <div className="mt-1 flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="ui-input pr-20 font-mono"
                            aria-invalid={Boolean(errors.password)}
                          />
                          <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
                            <button
                              type="button"
                              onClick={() => setShowPassword((s) => !s)}
                              className="rounded p-1.5 text-gray-400 hover:text-gray-600"
                              aria-label={showPassword ? 'Ascunde parola' : 'Arată parola'}
                            >
                              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => copyPassword(password)}
                              className="rounded p-1.5 text-gray-400 hover:text-gray-600"
                              aria-label="Copiază parola"
                            >
                              {copied ? (
                                <Check size={15} className="text-emerald-500" />
                              ) : (
                                <Copy size={15} />
                              )}
                            </button>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={regeneratePassword}
                          className="ui-btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <RefreshCw size={14} />
                          Generează
                        </button>
                      </div>
                      {errText('password')}
                    </div>
                  )}

                  {/* Send welcome email (manual mode only) */}
                  {accountMode === 'password' && (
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={sendWelcomeEmail}
                        onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      Trimite email de bun venit cu datele de autentificare
                    </label>
                  )}

                  {/* Configure RBAC (inline expandable) */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setRbacOpen((o) => !o)}
                      className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-accent"
                    >
                      <span>
                        Configurează RBAC
                        {templateId && templates ? (
                          <span className="ml-2 font-normal text-gray-400">
                            {templates.find((t) => t.id === templateId)?.name ?? ''}
                          </span>
                        ) : null}
                      </span>
                      {rbacOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {rbacOpen && (
                      <div className="mt-3">
                        <RbacMatrixEditor
                          value={matrix}
                          onChange={setMatrix}
                          templates={templates ?? []}
                          templateId={templateId}
                          onSelectTemplate={handleSelectTemplate}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {submitError && <p className="mt-4 text-sm text-red-600">{submitError}</p>}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              Anulează
            </button>
            <button
              type="submit"
              disabled={!isValid || create.isPending}
              className="ui-btn-primary"
            >
              {create.isPending ? 'Se creează…' : 'Creează membru'}
            </button>
          </div>
        </form>
      </div>

      {/* Invite confirmation dialog. */}
      {inviteDialog && (
        <div
          className="ui-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setInviteDialog(null);
              onClose();
            }
          }}
        >
          <div className="ui-modal-panel w-full max-w-[440px] p-5">
            <h3 className="text-base font-semibold text-gray-900">Membru creat</h3>
            <p className="mt-1 text-sm text-gray-500">
              Invitație trimisă la{' '}
              <span className="font-medium text-gray-900">{inviteDialog.email}</span>. Persoana își
              va seta parola din linkul primit (valabil 7 zile).
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setInviteDialog(null);
                  onClose();
                }}
                className="ui-btn-primary"
              >
                Gata
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password confirmation dialog (SMTP unconfigured / email opted out). */}
      {passwordDialog && (
        <div
          className="ui-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setPasswordDialog(null);
              onClose();
            }
          }}
        >
          <div className="ui-modal-panel w-full max-w-[440px] p-5">
            <h3 className="text-base font-semibold text-gray-900">Membru creat</h3>
            <p className="mt-1 text-sm text-gray-500">
              Email-ul de bun venit nu a putut fi trimis (SMTP nu este configurat) sau a fost omis.
              Distribuie aceste date de autentificare în siguranță — această parolă nu va mai fi
              afișată.
            </p>
            <div className="mt-4 space-y-2 rounded-lg border border-border bg-gray-50 p-3 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-gray-500">Email</span>
                <span className="font-medium text-gray-900">{passwordDialog.email}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-500">Parolă</span>
                <span className="flex items-center gap-2">
                  <code className="rounded bg-white px-2 py-0.5 font-mono text-gray-900">
                    {passwordDialog.password}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyPassword(passwordDialog.password)}
                    className="rounded p-1 text-gray-400 hover:text-gray-600"
                    aria-label="Copiază parola"
                  >
                    {copied ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
                  </button>
                </span>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setPasswordDialog(null);
                  onClose();
                }}
                className="ui-btn-primary"
              >
                Gata
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
